/* Answer vocabulary — the set of status categories a run may emit.
 *
 * The app used to hardcode "Met" / "Partial" / "Gap" in thirteen places, and
 * `normalizeRow` silently coerced anything else to "Gap". That is a compliance
 * problem: an RFP that mandates its own response categories ("Native / Out of
 * the Box", "Configuration Required", …) cannot be scored against other vendors
 * if we answer in a different vocabulary, and the silent coercion turned a
 * compliant answer into a red cell that skipped the review queue.
 *
 * A vocabulary is a list of categories, each mapped to one of three COLOR ROLES
 * so the traffic-light rendering stays fixed while the words vary:
 *
 *   met     → green   requirement is fully satisfied
 *   partial → amber   satisfied with a caveat, or unknown/unmapped
 *   gap     → red     not satisfied, or needs customer input
 */

export const ROLES = ['met', 'partial', 'gap'];

/** Fallback when a category cannot be mapped — amber, never a false green/red. */
export const UNKNOWN_ROLE = 'partial';

export const DEFAULT_VOCAB = {
  source: 'default',
  categories: [
    {
      label: 'Met',
      role: 'met',
      definition:
        'The product/service FULLY meets the requirement as described in the RKB.',
    },
    {
      label: 'Partial',
      role: 'partial',
      definition:
        'The product/service PARTIALLY meets the requirement — some aspect is missing, '
        + 'requires customization, is roadmap-only, or has a material limitation. State what '
        + 'IS supported and explicitly note the gap/caveat.',
    },
    {
      label: 'Gap',
      role: 'gap',
      definition:
        'The product/service does NOT meet the requirement, OR the question requires '
        + 'customer-specific input that only the account team can provide (named references, '
        + 'pricing, specific quotes, custom SOW language). For customer-input Gaps, prefix the '
        + 'answer with "[NEEDS YOUR INPUT — <what is needed>]".',
    },
  ],
};

/** Words that hint at a color role, most specific first. */
const ROLE_HINTS = [
  [/not support|unsupport|no\b|none|gap|not met|not available|cannot|unable/i, 'gap'],
  [/custom development|third.?party|isv|roadmap|future|partial|configuration required|workaround/i, 'partial'],
  [/native|out of the box|standard|included|full|yes\b|met|compliant|supported/i, 'met'],
];

/** Best-effort role for a category label the model invented. */
export function inferRole(label) {
  for (const [re, role] of ROLE_HINTS) {
    if (re.test(label)) return role;
  }
  return UNKNOWN_ROLE;
}

/**
 * Build a validated vocabulary from raw category data (model output or config).
 * Returns null when nothing usable is supplied, so callers fall back to DEFAULT_VOCAB.
 */
export function makeVocab(raw, source = 'rfp') {
  const list = Array.isArray(raw) ? raw : [];
  const categories = [];
  const seen = new Set();

  for (const item of list) {
    const label = typeof item === 'string' ? item.trim() : String(item?.label ?? '').trim();
    if (!label || label.length > 80) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    const declared = typeof item?.role === 'string' ? item.role.toLowerCase() : '';
    const role = ROLES.includes(declared) ? declared : inferRole(label);
    const definition = typeof item?.definition === 'string' ? item.definition.trim() : '';
    categories.push({ label, role, definition });
  }

  if (categories.length < 2) return null;
  return { source, categories: categories.slice(0, 12) };
}

export function labelsOf(vocab) {
  return vocab.categories.map((c) => c.label);
}

/**
 * Canonicalize a status string the model returned. Case- and whitespace-
 * insensitive. Returns null when it is not in the vocabulary at all — callers
 * must decide what to do rather than being handed a silent substitution.
 */
export function matchStatus(vocab, raw) {
  if (typeof raw !== 'string') return null;
  const needle = raw.trim().toLowerCase();
  if (!needle) return null;
  const hit = vocab.categories.find((c) => c.label.toLowerCase() === needle);
  return hit ? hit.label : null;
}

/** Color role for a status label; amber for anything unrecognized. */
export function roleOf(vocab, label) {
  const hit = vocab.categories.find((c) => c.label === label);
  return hit ? hit.role : UNKNOWN_ROLE;
}

/**
 * The category to use when we could not answer at all (batch failure, missing
 * row). Prefers a red category, else the last one listed.
 */
export function gapLabel(vocab) {
  const red = vocab.categories.find((c) => c.role === 'gap');
  return (red || vocab.categories[vocab.categories.length - 1]).label;
}

/**
 * A representative label for a color role, used to keep the few-shot examples
 * in the system prompt speaking the run's actual vocabulary.
 */
export function labelForRole(vocab, role) {
  const hit = vocab.categories.find((c) => c.role === role);
  return (hit || vocab.categories[0]).label;
}

/** Zeroed tally keyed by every label in the vocabulary — never yields NaN. */
export function emptyTally(vocab) {
  const t = {};
  for (const c of vocab.categories) t[c.label] = 0;
  return t;
}
